import { useState } from 'react';
import { color } from '@/themes/color';
import { typography } from '@/themes/typography';
import logoUrl from '@/assets/Logos/logo.webp';
import type { ReactNode } from 'react';
import CustomButton from '../Button/button';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

function MenuIcon(): JSX.Element {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"
        fill="currentColor"
      />
    </svg>
  );
}

function CloseIcon(): JSX.Element {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export interface NavLinkItem {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface NavbarProps {
  logo?: ReactNode;
  links?: readonly NavLinkItem[];
  ctaLabel?: string;
  ctaHref?: string;
  ctaOnClick?: () => void;
  logoHref?: string | null;
  className?: string;
  minimal?: boolean;
}

const DEFAULT_LINKS: readonly NavLinkItem[] = [
  { label: 'How it Works', href: '#how-it-works' },
  { label: 'Partners', href: '#partners' },
];

const linkSx = {
  fontFamily: "'Gotham', sans-serif",
  color: color.charcoal.default,
  textDecoration: 'none',
  fontSize: typography.heading4.fontSize,
  fontWeight: 350,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  paddingLeft: '12px',
  paddingRight: '12px',
  whiteSpace: 'nowrap',
  '&:hover': { color: color.terracotta.default, transition: 'color 0.3s ease' },
};

export default function Navbar({
  logo,
  links = DEFAULT_LINKS,
  ctaLabel = 'Download App',
  ctaHref,
  ctaOnClick,
  logoHref = '/',
  minimal = false,
}: NavbarProps): JSX.Element {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = (): void => {
    setMobileOpen(false);
  };

  const handleNavClick = (item: NavLinkItem): void => {
    item.onClick?.();
    closeMobile();
  };

  const drawer = (
    <Box
      sx={{
        width: 280,
        pt: 1,
        px: 1,
        paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))',
        backgroundColor: color.cream['100'],
        minHeight: '97%',
        display: 'flex',
        flexDirection: 'column',
      }}
      role="presentation"
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <IconButton
          type="button"
          onClick={closeMobile}
          aria-label="Close menu"
          sx={{ color: color.charcoal.default }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <List disablePadding sx={{ flex: 1 }}>
        {links.map((item) => (
          <ListItemButton
            key={item.label}
            {...(item.href
              ? {
                  component: 'a' as const,
                  href: item.href,
                  onClick: () => handleNavClick(item),
                }
              : {
                  component: 'button' as const,
                  type: 'button' as const,
                  onClick: () => handleNavClick(item),
                })}
            sx={{
              borderRadius: '8px',
              py: 1.5,
              '&:hover': { bgcolor: `${color.terracotta['100']}80` },
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                sx: {
                  fontFamily: "'Gotham', sans-serif",
                  fontSize: typography.heading4.fontSize,
                  fontWeight: 350,
                  color: color.charcoal.default,
                },
              }}
            />
          </ListItemButton>
        ))}
      </List>
      {(ctaHref || ctaOnClick) && (
        <>
          <Divider sx={{ my: 2, borderColor: color.grey['60'] }} />
          <Box sx={{ px: 1 }}>
            {ctaHref ? (
              <CustomButton
                variant="contained"
                fullWidth
                href={ctaHref}
                padding="14px 20px"
                onClick={() => {
                  gtag('event', 'cta_click', {
                    event_category: 'Engagement',
                    event_label: 'Download App – Navbar Mobile',
                  });
                  closeMobile();
                }}
              >
                {ctaLabel}
              </CustomButton>
            ) : (
              <CustomButton
                variant="contained"
                fullWidth
                padding="14px 20px"
                onClick={() => {
                  gtag('event', 'cta_click', {
                    event_category: 'Engagement',
                    event_label: 'Download App – Navbar Mobile',
                  });
                  ctaOnClick?.();
                  closeMobile();
                }}
              >
                {ctaLabel}
              </CustomButton>
            )}
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box
      component="header"
      sx={{ position: 'relative', zIndex: theme.zIndex.appBar }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          paddingLeft: { xs: '16px', sm: '24px', md: '32px' },
          paddingRight: { xs: '16px', sm: '24px', md: '32px' },
          paddingTop: '10px',
          paddingBottom: '10px',
          backgroundColor: color.cream['100'],
        }}
      >
        <Box
          {...(logoHref
            ? {
                component: 'a' as const,
                href: logoHref,
              }
            : {})}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            lineHeight: 0,
            textDecoration: 'none',
            cursor: logoHref ? 'pointer' : 'default',
          }}
          aria-label={logoHref ? 'UK Vineyard Guide home' : undefined}
        >
          {logo ?? (
            <Box
              component="img"
              src={logoUrl}
              alt="UK Vineyard Guide"
              sx={{
                width: { xs: 150, sm: 190, md: 220 },
                height: 'auto',
                display: 'block',
              }}
            />
          )}
        </Box>

        {!minimal &&
          (isDesktop ? (
            <Box
              component="nav"
              aria-label="Main navigation"
              display="flex"
              alignItems="center"
              gap={{ md: 1, lg: '10px' }}
              flexShrink={0}
            >
              {links.map((item) =>
                item.href ? (
                  <Typography
                    key={item.label}
                    component="a"
                    href={item.href}
                    onClick={item.onClick}
                    sx={linkSx}
                  >
                    {item.label}
                  </Typography>
                ) : (
                  <Typography
                    key={item.label}
                    component="button"
                    type="button"
                    sx={linkSx}
                    onClick={item.onClick}
                  >
                    {item.label}
                  </Typography>
                )
              )}
              {(ctaHref || ctaOnClick) &&
                (ctaHref ? (
                  <CustomButton
                    variant="contained"
                    href={ctaHref}
                    padding="14px 24px"
                    onClick={() => {
                      gtag('event', 'cta_click', {
                        event_category: 'Engagement',
                        event_label: 'Download App – Navbar Desktop',
                      });
                    }}
                  >
                    {ctaLabel}
                  </CustomButton>
                ) : (
                  <CustomButton
                    variant="contained"
                    onClick={() => {
                      gtag('event', 'cta_click', {
                        event_category: 'Engagement',
                        event_label: 'Download App – Navbar Desktop',
                      });
                      ctaOnClick?.();
                    }}
                    padding="14px 24px"
                  >
                    {ctaLabel}
                  </CustomButton>
                ))}
            </Box>
          ) : (
            <IconButton
              type="button"
              color="inherit"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              aria-controls="navbar-mobile-menu"
              onClick={() => setMobileOpen(true)}
              sx={{ color: color.charcoal.default, flexShrink: 0 }}
            >
              <MenuIcon />
            </IconButton>
          ))}
      </Box>

      <Drawer
        id="navbar-mobile-menu"
        anchor="right"
        open={mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: {
            backgroundColor: color.cream['100'],
            boxSizing: 'border-box',
          },
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
